import { useEffect, useState } from 'react';

/**
 * Rayon (en mètres) en-deçà duquel on considère l'utilisateur « sur place ».
 * Volontairement strict : on ne masque le bouton « Venir » que si on est
 * quasiment à l'entrée, pas dans le pâté de maisons voisin.
 */
export const AT_VENUE_METERS = 20;

/**
 * Distance en mètres entre deux points GPS (formule de Haversine).
 * Même formule que `haversineM` côté backend (packages/backend/src/routes/hunts.ts)
 * pour rester cohérent d'un bout à l'autre.
 */
export function haversineMeters(lat1: number, lng1: number, lat2: number, lng2: number): number {
  const R = 6371000; // rayon terrestre moyen en mètres
  const toRad = (d: number) => (d * Math.PI) / 180;
  const dLat = toRad(lat2 - lat1);
  const dLng = toRad(lng2 - lng1);
  const a =
    Math.sin(dLat / 2) ** 2 +
    Math.cos(toRad(lat1)) * Math.cos(toRad(lat2)) * Math.sin(dLng / 2) ** 2;
  return R * 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
}

/**
 * `true` si l'utilisateur est physiquement à moins de `radiusM` mètres du point
 * (lat/lng). Sert à masquer le bouton « Venir » quand on est déjà sur place.
 *
 * Suivi en direct : on utilise `watchPosition` plutôt qu'une mesure unique, de
 * sorte que l'encart se met à jour quand l'utilisateur bouge (arrive au bar ou
 * en repart). L'état repasse donc à `false` dès qu'on s'éloigne au-delà du seuil.
 *
 * Sûreté par défaut : renvoie `false` tant qu'on n'a pas la preuve d'être sur
 * place (géoloc refusée, indisponible, hors zone, coords tenant absentes). Le
 * bouton reste donc affiché dans tous les cas ambigus.
 *
 * Non intrusif : on ne déclenche la demande de permission native (via
 * `watchPosition`) que si l'état de permission n'est pas déjà `denied`. Un refus
 * n'est donc jamais redemandé, et `enableHighAccuracy: false` évite d'allumer le
 * GPS haute précision pour une simple vérification de proximité.
 *
 * Retour au premier plan : `watchPosition` est gelé en arrière-plan sur iOS
 * Safari. On force donc un `getCurrentPosition` ponctuel sur `visibilitychange`
 * pour rafraîchir l'état sans attendre le prochain tick du suivi.
 */
export function useIsAtLocation(
  lat: number | undefined,
  lng: number | undefined,
  radiusM = AT_VENUE_METERS,
): boolean {
  const [atLocation, setAtLocation] = useState(false);

  useEffect(() => {
    if (lat == null || lng == null) return;
    if (typeof navigator === 'undefined' || !navigator.geolocation) return;

    let cancelled = false;
    let watchId: number | null = null;

    const options: PositionOptions = {
      enableHighAccuracy: false,
      maximumAge: 60000,
      timeout: 10000,
    };

    // Applique une mesure : met à jour l'état dans les deux sens (sur place /
    // parti). C'est ce qui fait vivre l'encart quand l'utilisateur se déplace.
    const apply = (pos: GeolocationPosition) => {
      if (cancelled) return;
      const d = haversineMeters(pos.coords.latitude, pos.coords.longitude, lat, lng);
      setAtLocation(d < radiusM);
    };

    // Refus, timeout ou position indisponible : on ne redemande jamais et on
    // laisse `atLocation` tel quel (repli sûr → bouton affiché au démarrage).
    const onError = () => {};

    const onVisibility = () => {
      // Coup ponctuel au retour au premier plan (watchPosition gelé en fond iOS).
      if (document.visibilityState === 'visible') {
        navigator.geolocation.getCurrentPosition(apply, onError, options);
      }
    };

    const begin = () => {
      if (cancelled) return;
      watchId = navigator.geolocation.watchPosition(apply, onError, options);
      document.addEventListener('visibilitychange', onVisibility);
    };

    // L'API Permissions permet d'éviter le prompt agressif : si l'utilisateur a
    // déjà refusé, on ne démarre pas le suivi (qui re-demanderait).
    if (navigator.permissions?.query) {
      navigator.permissions
        .query({ name: 'geolocation' as PermissionName })
        .then((status) => {
          if (!cancelled && status.state !== 'denied') begin();
        })
        .catch(() => {
          if (!cancelled) begin();
        });
    } else {
      begin();
    }

    return () => {
      cancelled = true;
      if (watchId != null) navigator.geolocation.clearWatch(watchId);
      document.removeEventListener('visibilitychange', onVisibility);
    };
  }, [lat, lng, radiusM]);

  return atLocation;
}
